import { neverHere } from "./tsHelpers";

const integerRegexp = /^\d+$/;
const rangeRegexp = /^(\d+)?\.\.(\d+)?$/;

/** Convert a range string to a range object
 *  Note that negative range endpoints (-5..7, -23..-11) are not supported in this version.
 */
export function stringToNumericRange(inputValue: string): INumericRange | null {
  if (!inputValue) { return null; }

  // simple numeric => Equal
  if (integerRegexp.test(inputValue)) {
    const value = parseInt(inputValue, 10);
    return {
      type: NumericRangeType.Equal,
      value,
    };
  }

  const matches = inputValue.match(rangeRegexp);
  if (!matches) { return null; }

  const [ fullMatch, a, b ] = matches; // fullMatch not used
  let aInt = parseInt(a, 10);
  let bInt = parseInt(b, 10);

  if (a != null && b != null) {
    // a..b
    if (aInt === bInt) {
      return {
        type: NumericRangeType.Equal,
        value: aInt,
      };
    }
    else {
      let reversed;
      if (aInt > bInt) {
        [aInt, bInt] = [bInt, aInt];
        reversed = true;
      }
      const result: INumericRange = {
        type: NumericRangeType.Between,
        min: aInt,
        max: bInt,
      };
      if (reversed) { result.reversed = true; }
      return result;
    }
  }
  else if (a != null) {
    // a..
    return {
      type: NumericRangeType.GreaterThanOrEqual,
      min: aInt,
    };
  }
  else if (b != null) {
    // ..b
    return {
      type: NumericRangeType.LessThanOrEqual,
      max: bInt,
    };
  }
  else {
    // ..
    return null;
  }
}

/** Convert a range object to a range string */
export function numericRangeToString(range: INumericRange): string {
  switch (range.type) {
    case NumericRangeType.Equal:
      return `${range.value}`;
    case NumericRangeType.Between:
      if (range.reversed) {
        return `${range.max}..${range.min}`;
      }
      else {
        return `${range.min}..${range.max}`;
      }
    case NumericRangeType.GreaterThanOrEqual:
      return `${range.min}..`;
    case NumericRangeType.LessThanOrEqual:
      return `..${range.max}`;
    default:
      return neverHere(range);
  }
}

/**
 * Checks if a value is matches INumericRange type
 */
export function isNumericRange(x: any): x is INumericRange {
  if (!x) {
    return false;
  }

  switch (x.type) {
    case NumericRangeType.Equal:
    case NumericRangeType.Between:
    case NumericRangeType.GreaterThanOrEqual:
    case NumericRangeType.LessThanOrEqual:
      // Also check for the expected properties
      return (x.value != null && !isNaN(x.value))
        || (x.min != null && !isNaN(x.min))
        || (x.max != null && !isNaN(x.max))
        || (x.reversed != null && typeof x.reversed === "boolean")
        || true; // All of the properties above are optional
    default:
      return false;
  }
}
